import { useTranslateHeaders } from '@/ui/table/helpers/use-translate-headers';
import { useMemo } from 'react';
import { colors } from '@/theme/colors';
import { modalState } from '@/state/modal.state';
import type { SearchSuggestion } from '@/api/v4/search.api';
import { fileSuggestionsTableHeaders } from './headers-config';
import { TextCell } from '../enriched-table/text-cell';
import { hasProperty } from '@/helpers/has-property';
import { Button } from '@/ui/button/button';
import { type Row } from '@/ui/table/table.types';
import { format } from 'date-fns';
import { trackMixpanelSearchResultClicked } from '@/lib/mixpanel/mixpanel';

import { useSearchedItemClick } from '../use-searched-item-click';
import { toStartCase } from '@/helpers/strings';
import { FileContentCell } from './file-content-cell';
import { useNavigate } from 'react-router-dom';
import { paths } from '@/routes/helpers/paths';
import { useSetAtom } from 'jotai';

type FileSuggestion = SearchSuggestion & {
  entityName?: string;
  entityType?: string;
  content?: string;
  owner: string;
};

function isFileSuggestion(data: SearchSuggestion): data is FileSuggestion {
  return hasProperty('content', data);
}

export const useFileSuggestionsTableConfig = (data: SearchSuggestion[]) => {
  const setModal = useSetAtom(modalState);
  const headers = useTranslateHeaders(
    fileSuggestionsTableHeaders,
    'default',
    'globalSearch.suggestionsTable',
  );

  const handleSearchedItemClick = useSearchedItemClick();
  const navigate = useNavigate();

  const rows: Row[] = useMemo(() => {
    return data.map(basicData => {
      const isFileSuggestionType = isFileSuggestion(basicData);

      const handleRowClick = () => {
        setModal(null);
        if (basicData.entityId && basicData.entityType && basicData.id) {
          let path: string | null = null;
          switch (basicData.entityType) {
            case 'organization':
              trackMixpanelSearchResultClicked({
                entity_id: basicData.entityId,
                entity_type: 'company',
                entry_surface: 'global_search_files_table',
                is_recent: false,
              });
              path = paths.company({ companyId: basicData.entityId });
              break;
            case 'project':
              trackMixpanelSearchResultClicked({
                entity_id: basicData.entityId,
                entity_type: 'project',
                entry_surface: 'global_search_files_table',
                is_recent: false,
              });
              path = paths.project({ projectId: basicData.entityId });
              break;
            case 'team':
              trackMixpanelSearchResultClicked({
                entity_id: basicData.entityId,
                entity_type: 'team',
                entry_surface: 'global_search_files_table',
                is_recent: false,
              });
              path = paths.team({ teamId: basicData.entityId });
              break;
            default:
              return;
          }
          if (!path) return;
          const url = `${path}?actionPanelTab=files&file=${basicData.id}`;
          navigate(url);
        }
      };

      return {
        id: basicData.id,
        rowHover: true,
        rowBackground: colors.basics.white,
        rowBackgroundHover: colors.gray.c1,
        onClick: handleRowClick,
        cells: [
          {
            value: (
              <TextCell value={isFileSuggestionType ? basicData.name : ''} />
            ),
            padding: '0 12px',
          },
          {
            value: <TextCell value={basicData.entityName ?? ''} />,
            padding: '0 12px',
          },
          {
            value: <TextCell value={toStartCase(basicData.entityType)} />,
            padding: '0 12px',
          },
          {
            value: (
              <FileContentCell
                content={basicData.content}
                highlights={basicData.highlights.fileContent}
              />
            ),
            padding: '8px ',
          },

          {
            value: (
              <TextCell
                value={
                  isFileSuggestionType && basicData.uploadedDate
                    ? (format(
                        new Date(basicData.uploadedDate),
                        'dd MMM yyyy',
                      ) ?? '')
                    : ''
                }
              />
            ),
          },
          {
            value: (
              <TextCell
                value={isFileSuggestionType ? (basicData.owner ?? '') : ''}
              />
            ),
          },
          {
            value: (
              <Button
                onClick={async e => {
                  e.stopPropagation();
                  await handleSearchedItemClick(
                    {
                      type: basicData.type,
                      id: basicData.id,
                    },
                    basicData,
                  );
                }}
              >
                Open File
              </Button>
            ),
            hoverOnly: true,
          },
        ],
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    headers,
    rows,
  };
};
